import { createContext, FC, Key, useState, useMemo } from 'react'
import PageLayout from '../../components/page-layout/page-layout'
import { useForm } from 'antd/es/form/Form'
import { ConfigProvider, Form } from 'antd'
import styled from 'styled-components'
import TableWrapper from '../../components/table-wrapper/table-wrapper'
import { useGetShipmentsQuery } from '../../store/api/shipments-api'
import { EditableCell } from '../../components/editable-cell/editable-cell'
import GroupOperations from './components/filters-form/components/group-operations'
import FilterForm from './components/filters-form/filters-forms'
import useFiltersForm from '../../hooks/forms/use-filters-form'
import useEndpointQueryParams from '../../hooks/table-pages/use-endpoint-query-parameters'
import Shipment from '../../types/api/shipment'
import { TableWithColumnManager } from '../../components/table-with-column-manager/table-with-column-manager'
import { columns } from './constants/columns'
import { statusColumn } from './constants/columns/status'
import { defaultParams } from './constants/default-params'
import { setEditing } from '../../helpers/tables/set-editing'
import { deserializeFilters } from './helpers/deserialize-filters'
import { ExpandedRow } from './components/expanded-row/expanded-row'
import { FormValues } from '../../helpers/tables/get-data-for-update'
import { configProviderTheme } from './constants/config-provider-theme'
import { LocalStorageKeys } from '../../constants/local-storage-keys'
import { ToolingKind } from './helpers/tooling-inclusion'
import { useUpdateShipmentProperty } from './hooks/use-update-shipment-property'
import { ShipmentUpdateData, useUpdateShipmentFields } from './hooks/use-update-shipment-fields'
import {
    ShipmentPositionUpdateData,
    useUpdateShipmentPosition,
} from './hooks/use-update-shipment-position'
import { useSetToolingIncluded } from './hooks/use-set-tooling-included'
import useTablePagination from '../../hooks/table-pages/use-table-pagination'
import { ShipmentsPaginationTotal } from './components/shipments-pagination-total'
import HelpDrawer from './components/help-drawer/help-drawer'

const ShipmentsTableArea = styled.div`
    flex: auto;
    min-height: 0;
    display: flex;
    flex-direction: column;

    .ant-table-wrapper .ant-table-pagination.shipments-table-pagination {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    .ant-table-wrapper .ant-table-pagination.shipments-table-pagination .ant-pagination-total-text {
        flex: 1 1 auto;
        min-width: 0;
        margin-inline-end: auto;
        text-align: left;
    }
`

export type DataSourceItem = Shipment

interface ShipmentsPageCtxValue {
    onUpdateShipmentProperty: (record: Shipment, formValues: FormValues) => void
    /** Сохранение без пересчёта отгрузки — для флагов, не влияющих на расчёт */
    onUpdateShipmentFields: (record: Shipment, data: ShipmentUpdateData) => void
    onUpdateShipmentPosition: (
        record: Shipment,
        positionDocId: string,
        data: ShipmentPositionUpdateData
    ) => void
    onSetToolingIncluded: (record: Shipment, kind: ToolingKind, included: boolean) => Promise<void>
}

export const ShipmentsPageCtx = createContext<ShipmentsPageCtxValue>({
    onUpdateShipmentProperty: () => {},
    onUpdateShipmentFields: () => {},
    onUpdateShipmentPosition: () => {},
    onSetToolingIncluded: async () => {},
})

interface ShipmentsPageProps {}

const ShipmentsPage: FC<ShipmentsPageProps> = () => {
    const [form] = useForm()
    // @ts-ignore
    window.sform = form
    const params = useEndpointQueryParams({ deserializeFilters })
    const queryArg = { ...params, ...defaultParams }

    const { data } = useGetShipmentsQuery(queryArg, { refetchOnMountOrArgChange: true })
    const shipments = data?.data || []
    const basePagination = useTablePagination(queryArg, data)

    const pagination = useMemo(
        () => ({
            ...basePagination,
            className: 'shipments-table-pagination',
            showTotal: (total: number) => <ShipmentsPaginationTotal total={total} />,
        }),
        [basePagination]
    )

    const dataSource: DataSourceItem[] = shipments

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
    const [selectedRows, setSelectedRows] = useState<DataSourceItem[]>([])
    const { form: filterForm, onSearch, onClear } = useFiltersForm()
    const [columnManagerSlot, setColumnManagerSlot] = useState<HTMLDivElement | null>(null)

    const tableComponents = useMemo(() => ({ body: { cell: EditableCell } }), [])

    /** Вызывается из EditableCell и обновляет данные в таблице */
    const onUpdateShipmentProperty = useUpdateShipmentProperty(queryArg)
    const onUpdateShipmentFields = useUpdateShipmentFields(queryArg)
    const onUpdateShipmentPosition = useUpdateShipmentPosition(queryArg)
    const onSetToolingIncluded = useSetToolingIncluded(queryArg)

    const columnsForRender = useMemo(() => {
        // Запрещаем редактирование, если отгрузка спущена в печать
        const isRowEditable = (record: DataSourceItem) => !Boolean(record.print_job)

        const withGroupOperations = columns.map(column =>
            column.key === statusColumn.key
                ? {
                      ...column,
                      title: selectedRows.length > 0 && (
                          <GroupOperations selectedRows={selectedRows} />
                      ),
                  }
                : column
        )

        return setEditing<DataSourceItem>(
            withGroupOperations,
            onUpdateShipmentProperty,
            isRowEditable
        )
    }, [onUpdateShipmentProperty, selectedRows])

    return (
        <ShipmentsPageCtx.Provider
            value={{
                onUpdateShipmentProperty,
                onUpdateShipmentFields,
                onUpdateShipmentPosition,
                onSetToolingIncluded,
            }}
        >
            <PageLayout title={'Отгрузки'} beforeUser={<HelpDrawer />}>
                <div>
                    <FilterForm
                        form={filterForm}
                        onSearch={onSearch}
                        onClear={onClear}
                        columnManagerSlotRef={setColumnManagerSlot}
                    />
                </div>
                <Form style={{ flex: 'auto' }} form={form} component={false}>
                    <ConfigProvider theme={configProviderTheme}>
                        <ShipmentsTableArea>
                            <TableWrapper style={{ flex: 'auto' }} filtersSummaryHeight={0}>
                                <TableWithColumnManager
                                    storageKey={LocalStorageKeys.ShipmentsTableColumns}
                                    bordered
                                    columns={columnsForRender}
                                    dataSource={dataSource}
                                    components={tableComponents}
                                    rowKey={x => x.id}
                                    pagination={pagination}
                                    size={'small'}
                                    columnManagerContainer={columnManagerSlot}
                                    rowSelection={{
                                        selectedRowKeys,
                                        onChange: (selectedRowKeys, selectedRows) => {
                                            setSelectedRowKeys(selectedRowKeys)
                                            setSelectedRows(selectedRows)
                                        },
                                    }}
                                    expandable={{
                                        expandedRowRender: (record: DataSourceItem) => (
                                            <ExpandedRow record={record} />
                                        ),
                                        rowExpandable: () => true,
                                    }}
                                />
                            </TableWrapper>
                        </ShipmentsTableArea>
                    </ConfigProvider>
                </Form>
            </PageLayout>
        </ShipmentsPageCtx.Provider>
    )
}

export default ShipmentsPage
